/**
 * Compliance → CCPA opt-outs (§1798.120) - Phase 8 batch G item 2.
 *
 * California (and 9 other US states) opt-out registry. Active on create;
 * subject can opt back in via revoke. Same SHA-256(kind|raw) principal
 * hash as the subject-rights quintet so the audit chain joins.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, XCircle, Plus, Eye, Undo2, Ban, ShieldOff,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listCcpaOptouts, createCcpaOptout, revokeCcpaOptout,
  type CcpaOptout, type CcpaJurisdiction, type CcpaRequestChannel,
  type ErasureIdentifierKind,
} from '@/lib/api';

const IDENTIFIERS: ReadonlyArray<{ value: ErasureIdentifierKind; label: string }> = [
  { value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' },
  { value: 'aadhaar', label: 'Aadhaar' }, { value: 'pan', label: 'PAN' },
  { value: 'customer_number', label: 'Customer number' }, { value: 'custom', label: 'Custom' },
];
const JURISDICTIONS: ReadonlyArray<CcpaJurisdiction> = [
  'CA', 'CO', 'VA', 'CT', 'UT', 'OR', 'TX', 'FL', 'DE', 'MT',
];
const CHANNELS: ReadonlyArray<CcpaRequestChannel> = [
  'webform', 'authorized_agent', 'gpc_signal', 'phone', 'paper',
];

export function ComplianceCcpa(): JSX.Element {
  const [items, setItems] = useState<CcpaOptout[]>([]);
  const [counts, setCounts] = useState({ active: 0, revoked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CcpaOptout | null>(null);

  const [identifier, setIdentifier] = useState('');
  const [kind, setKind] = useState<ErasureIdentifierKind>('email');
  const [jurisdiction, setJurisdiction] = useState<CcpaJurisdiction>('CA');
  const [channel, setChannel] = useState<CcpaRequestChannel>('webform');
  const [optSale, setOptSale] = useState(true);
  const [optShare, setOptShare] = useState(true);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await listCcpaOptouts({}); setItems(r.optouts); setCounts(r.byStatus); }
    catch (e) { setError(e instanceof Error ? e.message : 'failed to load opt-outs'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function handleSubmit() {
    if (!identifier.trim()) { setError('Principal identifier is required'); return; }
    if (!optSale && !optShare) { setError('At least one channel must be opted out'); return; }
    setSubmitting(true); setError(null);
    try {
      await createCcpaOptout({
        principalIdentifier: identifier.trim(), identifierKind: kind,
        optOutSale: optSale, optOutSharing: optShare,
        jurisdiction, requestChannel: channel,
        ...(reason.trim() ? { requestReason: reason.trim() } : {}),
      });
      setIdentifier(''); setReason('');
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : 'submit failed'); }
    finally { setSubmitting(false); }
  }

  async function handleRevoke(o: CcpaOptout) {
    const r = window.prompt('Reason for revoking opt-out (subject opting back in):');
    if (!r) return;
    try { await revokeCcpaOptout(o.id, r); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : 'revoke failed'); }
  }

  return (
    <PageContainer>
      <PageHeader
        title="CCPA opt-out registry (§1798.120)"
        subtitle="California (+ 9 other state) opt-out of sale / sharing of personal information. Active on submit; takes effect immediately per §1798.135. Detection-side firewall honours active rows on every query."
        badges={[
          { label: 'Active on submit', variant: 'glass' },
          { label: 'GPC signal aware', variant: 'glass' },
          ...(loading ? [{ label: 'Loading', variant: 'glass' as const }]
            : error ? [{ label: 'Load error', variant: 'warning' as const }]
            : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={<Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>}
      />
      {error && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">{error}</div>
          <Button size="sm" variant="glass" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <KpiCard label="Active" value={counts.active} icon={ShieldOff} glow={counts.active > 0 ? 'high' : 'low'} hint="firewall live" />
        <KpiCard label="Revoked" value={counts.revoked} icon={Undo2} glow="low" hint="subject opted back" />
        <KpiCard label="Total" value={counts.active + counts.revoked} icon={CheckCircle2} glow="low" hint="lifetime opt-outs" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Submit opt-out</CardTitle>
            <CardDescription>Identifier hashed on submit. Per §1798.135 the opt-out is effective immediately.</CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-muted-foreground block mb-0.5">Identifier kind</label>
                <select value={kind} onChange={(e) => setKind(e.target.value as ErasureIdentifierKind)}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40">
                  {IDENTIFIERS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select></div>
              <div><label className="text-muted-foreground block mb-0.5">Jurisdiction</label>
                <select value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value as CcpaJurisdiction)}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40">
                  {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
                </select></div>
            </div>
            <div><label className="text-muted-foreground block mb-0.5">Principal identifier</label>
              <input type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" />
              <div className="text-[10px] text-muted-foreground mt-0.5">Hashed at submit; raw never persists.</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[11px]">
              <label className="flex items-center gap-1"><input type="checkbox" checked={optSale} onChange={(e) => setOptSale(e.target.checked)} /> Opt out of <strong>sale</strong></label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={optShare} onChange={(e) => setOptShare(e.target.checked)} /> Opt out of <strong>sharing</strong></label>
            </div>
            <div><label className="text-muted-foreground block mb-0.5">Channel</label>
              <select value={channel} onChange={(e) => setChannel(e.target.value as CcpaRequestChannel)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40">
                {CHANNELS.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select></div>
            <div><label className="text-muted-foreground block mb-0.5">Reason (optional)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_ccpa.a01" onClick={() => void handleSubmit()} disabled={submitting}>
                {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldOff className="h-3.5 w-3.5 mr-1" />}
                Record opt-out
              </Button>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3"><CardTitle>Opt-out registry</CardTitle>
            <CardDescription clamp>Active rows feed the Detection firewall.</CardDescription></CardHeader>
          {loading && <LoadingState caption="Loading…" />}
          {!loading && items.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <ShieldOff className="h-6 w-6 mx-auto mb-2 opacity-40" /><div>No opt-outs yet.</div>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2">
              {items.map((o, i) => (
                <motion.div key={o.id} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }} className="glass-lite rounded p-3 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={o.status === 'active' ? 'success' : 'glass'} className="text-[10px]">{o.status}</Badge>
                        <Badge variant="glass" className="text-[10px]">{o.jurisdiction}</Badge>
                        <Badge variant="glass" className="text-[10px] font-mono">{o.identifierKind}</Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {o.optOutSale && 'sale'}{o.optOutSale && o.optOutSharing && ' + '}{o.optOutSharing && 'sharing'}
                        </span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate">{o.principalIdentifierHash.slice(0, 24)}…</div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="glass" onClick={() => setDetail(o)}><Eye className="h-3 w-3" /></Button>
                      {o.status === 'active' && <Button size="sm" variant="glass" data-action="disc.comp_ccpa.a02" onClick={() => void handleRevoke(o)} title="Revoke (subject opt-in)"><Undo2 className="h-3 w-3" /></Button>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
      <Dialog open={detail !== null} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>CCPA opt-out detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Status</div><Badge variant={detail.status === 'active' ? 'success' : 'glass'} className="text-[10px]">{detail.status}</Badge></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Jurisdiction</div><code className="font-mono">{detail.jurisdiction}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Identifier kind</div><code className="font-mono">{detail.identifierKind}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Channel</div><code className="font-mono">{detail.requestChannel ?? '-'}</code></div>
              </div>
              <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Principal hash</div><code className="font-mono text-[10px] break-all">{detail.principalIdentifierHash}</code></div>
              <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Channels opted out</div><div>{detail.optOutSale ? '✓ sale' : '- sale'} · {detail.optOutSharing ? '✓ sharing' : '- sharing'}</div></div>
              {detail.requestReason && <div className="glass-lite rounded p-2"><div className="text-muted-foreground">Reason</div><em>{detail.requestReason}</em></div>}
              {detail.revokedAt && (
                <div className="glass-lite rounded p-3"><div className="font-medium mb-1">Revoked</div>
                  <div className="text-muted-foreground">By <code className="font-mono">{detail.revokedByActorId?.slice(0, 8) ?? '?'}…</code> at {new Date(detail.revokedAt).toLocaleString()}</div>
                  {detail.revokeReason && <div className="mt-1">Reason: <em>{detail.revokeReason}</em></div>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
